import { always, defaultTo, not, path, pathOr } from 'rambda';
import { IRequestApiCallStatus, IRequestState } from '.';
import { IDeaEditMode, ISaveRequestOutput } from '../common';
import {
  compose as lcompose,
  filter as lfilter,
  find as lfind,
  get as lget,
  isEmpty as lisEmpty,
  eq as leq,
} from 'lodash/fp';
import { size } from 'fp-ts/ReadonlyArray';
import { left, right, Either } from 'fp-ts/Either';
import { getMonoidAll, getMonoidAny } from 'fp-ts/Predicate';
import { flow } from 'fp-ts/function';

export const validateMaxRequestDeaInfos =
  (max: number) =>
  (currentSize: number) =>
  (checked: boolean) =>
  (keysToSelect: string[]) =>
    checked ? keysToSelect.slice(0, max - currentSize) : keysToSelect;

export const selectedDeaInfoSize = flow(
  lfilter<IDeaEditMode>({ selected: true }),
  size
);

export const getCurrentRequestType = lcompose(
  defaultTo('ENROLL'),
  path<string>('request.reqType')
);

export const getCurrentRequestRole = lcompose(
  defaultTo(null),
  path<string>('request.role')
);

export const getCurrentRequestComments = lcompose(
  defaultTo(null),
  path<string>('request.comments')
);

export const getCurrentRequestSecondaryRole = lcompose(
  defaultTo(null),
  path<string>('request.secondaryRole')
);

export const findSaveRequestInput = (s: IRequestState) =>
  lfind<ISaveRequestOutput>({
    requestId: path<number>('request.requestId')(s),
  })(s.requests);

export const msgText = lcompose(
  defaultTo(null),
  path<string>('msgText'),
  findSaveRequestInput
);

export const requestStatus = lcompose(
  defaultTo('NEW'),
  path<string>('status'),
  findSaveRequestInput
);

export const requestDocumentName = lget('request.requestDocuments[0].fileName');

export const requiresEmploymentVerification = (evs: boolean) =>
  evs
    ? lcompose(
        defaultTo(false),
        path<boolean>('request.requiresEmploymentVerification')
      )
    : always(false);

export const requestIdToBase64 = lcompose(btoa, JSON.stringify);

export const requestIdFromBase64: (
  a: string | null
) => Either<undefined, number> = flow(
  (a: string | null) => {
    if (lisEmpty(a)) {
      return {};
    } else {
      try {
        return lcompose(JSON.parse, atob)(a);
      } catch (e) {
        return {};
      }
    }
  },
  lget('requestId'),
  defaultTo(0),
  b => (b === 0 ? left(void 0) : right(b))
);

const notInProgressConfirmSubmitRequest = flow(
  pathOr<IRequestApiCallStatus[]>([], 'apiCallStatus'),
  lfind<IRequestApiCallStatus>({
    type: 'ConfirmSubmitRequest',
    apiCallStatus: null,
  }),
  a => a === void 0
);
const notInProgressConfirmEmploymentVerification = flow(
  pathOr<IRequestApiCallStatus[]>([], 'apiCallStatus'),
  lfind<IRequestApiCallStatus>({
    type: 'ConfirmEmploymentVerification',
    apiCallStatus: null,
  }),
  a => a === void 0
);

const M = getMonoidAll<IRequestState>();

export const enableSetRequest = M.concat(
  notInProgressConfirmSubmitRequest,
  notInProgressConfirmEmploymentVerification
);

const enableEnrollStep2 = M.concat(
  flow(getCurrentRequestType, leq('ENROLL')),
  flow(getCurrentRequestRole, lisEmpty, not)
);
const enableRevokeStep2 = M.concat(
  flow(getCurrentRequestType, leq('REVOKE')),
  flow(getCurrentRequestComments, lisEmpty, not)
);
const enableRenewStep2 = flow(getCurrentRequestType, leq('RENEWAL'));
const MAny = getMonoidAny<IRequestState>();
export const enableStep2 = MAny.concat(
  MAny.concat(enableEnrollStep2, enableRevokeStep2),
  enableRenewStep2
);
